/**
 * ComparisonTable Component
 * Sortable table view for KPI comparison
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useComparisonStore } from '@/store'
import { SparklineChart } from '@/components/charts'
import { StatusBadge, ModelValidityDot, DataSourceIndicator } from '@/components/common'
import { Trophy, ArrowUpDown, ArrowUp, ArrowDown, AlertCircle } from 'lucide-react'
import clsx from 'clsx'

type SortField = 'reliability' | 'availability' | 'mtbf' | 'mttr' | 'anomaly_rate' | 'health_score'
type SortDirection = 'asc' | 'desc'

interface ComparisonTableProps {
  showSparklines?: boolean
}

export default function ComparisonTable({ showSparklines = true }: ComparisonTableProps) {
  const navigate = useNavigate()
  const { kpiComparison, trendComparison, sortOptions, setSortOptions } = useComparisonStore()
  const [hoveredAsset, setHoveredAsset] = useState<string | null>(null)

  const handleSort = (field: SortField) => {
    const direction: SortDirection =
      sortOptions.field === field && sortOptions.direction === 'desc' ? 'asc' : 'desc'
    setSortOptions({ field, direction })
  }

  const sortedAssets = useMemo(() => {
    if (!kpiComparison?.assets) return []

    const assets = [...kpiComparison.assets]
    const { field, direction } = sortOptions

    return assets.sort((a, b) => {
      let aVal = a.metrics[field as keyof typeof a.metrics] ?? 0
      let bVal = b.metrics[field as keyof typeof b.metrics] ?? 0

      // For MTTR and anomaly_rate, lower is better
      if (field === 'mttr' || field === 'anomaly_rate') {
        return direction === 'desc' ? aVal - bVal : bVal - aVal
      }

      return direction === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [kpiComparison?.assets, sortOptions])

  const getSortIcon = (field: SortField) => {
    if (sortOptions.field !== field) {
      return <ArrowUpDown size={12} className="text-surface-500" />
    }
    return sortOptions.direction === 'desc'
      ? <ArrowDown size={12} className="text-primary-400" />
      : <ArrowUp size={12} className="text-primary-400" />
  }

  const getStatusColor = (value: number, metric: string) => {
    if (metric === 'mttr') {
      return value <= 4 ? 'text-status-healthy' : value <= 8 ? 'text-status-warning' : 'text-status-critical'
    }
    if (metric === 'anomaly_rate') {
      return value <= 2 ? 'text-status-healthy' : value <= 5 ? 'text-status-warning' : 'text-status-critical'
    }
    // For reliability, availability, health_score
    return value >= 90 ? 'text-status-healthy' : value >= 75 ? 'text-status-warning' : 'text-status-critical'
  }

  const getSparklineData = (assetId: string, metric: string): number[] => {
    if (!trendComparison?.trends?.[assetId]) return []
    return trendComparison.trends[assetId][metric as keyof typeof trendComparison.trends[typeof assetId]] || []
  }

  const handleAssetClick = (assetId: string) => {
    navigate(`/assets/${assetId}`)
  }

  if (!kpiComparison || sortedAssets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-surface-400">
        Select assets to compare their KPIs
      </div>
    )
  }

  const columns = [
    { key: 'rank', label: 'Rank', sortable: false },
    { key: 'asset', label: 'Asset', sortable: false },
    { key: 'status', label: 'Status', sortable: false },
    { key: 'model', label: 'Model', sortable: false },
    { key: 'source', label: 'Source', sortable: false },
    { key: 'reliability', label: 'Reliability', sortable: true, unit: '%' },
    { key: 'availability', label: 'Availability', sortable: true, unit: '%' },
    { key: 'mtbf', label: 'MTBF', sortable: true, unit: 'h' },
    { key: 'mttr', label: 'MTTR', sortable: true, unit: 'h' },
    { key: 'anomaly_rate', label: 'Anomaly', sortable: true, unit: '%' },
    { key: 'health_score', label: 'Health', sortable: true, unit: '%' }
  ]

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-surface-700">
            {columns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  'text-left text-xs font-semibold text-surface-400 uppercase py-3 px-4',
                  col.sortable && 'cursor-pointer hover:text-surface-200 select-none'
                )}
                onClick={() => col.sortable && handleSort(col.key as SortField)}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {col.sortable && getSortIcon(col.key as SortField)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedAssets.map((asset, index) => {
            const rank = index + 1
            const isHovered = hoveredAsset === asset.asset_id
            const isBest = asset.asset_id === kpiComparison.best_performer
            const isWorst = asset.asset_id === kpiComparison.worst_performer

            return (
              <tr
                key={asset.asset_id}
                className={clsx(
                  'border-b border-surface-800 transition-colors',
                  isHovered && 'bg-surface-800/70',
                  isBest && 'bg-status-healthy/5',
                  isWorst && 'bg-status-critical/5'
                )}
                onMouseEnter={() => setHoveredAsset(asset.asset_id)}
                onMouseLeave={() => setHoveredAsset(null)}
              >
                {/* Rank */}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    {rank === 1 && <Trophy size={14} className="text-yellow-500" />}
                    {rank === 2 && <Trophy size={14} className="text-gray-400" />}
                    {rank === 3 && <Trophy size={14} className="text-amber-600" />}
                    <span className="text-surface-300 font-mono">{rank}</span>
                  </div>
                </td>

                {/* Asset ID */}
                <td className="py-3 px-4">
                  <button
                    onClick={() => handleAssetClick(asset.asset_id)}
                    className="font-mono text-primary-400 hover:text-primary-300 hover:underline"
                  >
                    {asset.asset_id}
                  </button>
                </td>

                {/* Status */}
                <td className="py-3 px-4">
                  <StatusBadge status={asset.status} size="sm" />
                </td>

                {/* Model Validity */}
                <td className="py-3 px-4">
                  <ModelValidityDot
                    isValid={asset.is_valid ?? true}
                    aucRoc={asset.model_auc_roc}
                  />
                </td>

                {/* Data Source */}
                <td className="py-3 px-4">
                  <DataSourceIndicator
                    source={asset.data_source ?? 'registry'}
                    size="sm"
                  />
                </td>

                {/* Reliability */}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className={clsx('font-mono', getStatusColor(asset.metrics.reliability, 'reliability'))}>
                      {asset.metrics.reliability.toFixed(1)}%
                    </span>
                    {showSparklines && getSparklineData(asset.asset_id, 'reliability').length > 0 && (
                      <SparklineChart
                        data={getSparklineData(asset.asset_id, 'reliability')}
                        width={50}
                        height={20}
                        showEndpoint={false}
                      />
                    )}
                  </div>
                </td>

                {/* Availability */}
                <td className="py-3 px-4">
                  <span className={clsx('font-mono', getStatusColor(asset.metrics.availability, 'availability'))}>
                    {asset.metrics.availability.toFixed(1)}%
                  </span>
                </td>

                {/* MTBF */}
                <td className="py-3 px-4">
                  <span className="font-mono text-surface-300">
                    {asset.metrics.mtbf.toFixed(0)}h
                  </span>
                </td>

                {/* MTTR */}
                <td className="py-3 px-4">
                  <span className={clsx('font-mono', getStatusColor(asset.metrics.mttr, 'mttr'))}>
                    {asset.metrics.mttr.toFixed(1)}h
                  </span>
                </td>

                {/* Anomaly Rate */}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className={clsx('font-mono', getStatusColor(asset.metrics.anomaly_rate, 'anomaly_rate'))}>
                      {asset.metrics.anomaly_rate.toFixed(1)}%
                    </span>
                    {showSparklines && getSparklineData(asset.asset_id, 'anomaly_score').length > 0 && (
                      <SparklineChart
                        data={getSparklineData(asset.asset_id, 'anomaly_score')}
                        width={50}
                        height={20}
                        showEndpoint={false}
                        positiveColor="#ef4444"
                        negativeColor="#10b981"
                      />
                    )}
                  </div>
                </td>

                {/* Health Score */}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className={clsx('font-mono', getStatusColor(asset.metrics.health_score, 'health_score'))}>
                      {asset.metrics.health_score.toFixed(0)}%
                    </span>
                    {showSparklines && getSparklineData(asset.asset_id, 'health_score').length > 0 && (
                      <SparklineChart
                        data={getSparklineData(asset.asset_id, 'health_score')}
                        width={50}
                        height={20}
                        showEndpoint={false}
                      />
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Fleet Average Row */}
      {kpiComparison?.fleet_average && (
        <div className="mt-4 p-3 bg-surface-800/50 rounded-lg border border-surface-700">
          <div className="flex items-center gap-6 text-sm">
            <span className="text-surface-400 font-medium">Fleet Average:</span>
            <span className="text-surface-300">
              Reliability: <span className="font-mono text-primary-400">{(kpiComparison.fleet_average?.reliability ?? 0).toFixed(1)}%</span>
            </span>
            <span className="text-surface-300">
              Availability: <span className="font-mono text-primary-400">{(kpiComparison.fleet_average?.availability ?? 0).toFixed(1)}%</span>
            </span>
            <span className="text-surface-300">
              MTBF: <span className="font-mono text-primary-400">{(kpiComparison.fleet_average?.mtbf ?? 0).toFixed(0)}h</span>
            </span>
          </div>
        </div>
      )}

      {/* Data Source Metadata & Invalid Models Warning */}
      {(kpiComparison?.invalid_models_excluded !== undefined && kpiComparison.invalid_models_excluded > 0) && (
        <div className="mt-3 p-3 bg-status-warning/10 rounded-lg border border-status-warning/30">
          <div className="flex items-center gap-2 text-sm text-status-warning">
            <AlertCircle size={14} />
            <span>
              {kpiComparison.invalid_models_excluded} asset{kpiComparison.invalid_models_excluded > 1 ? 's' : ''} excluded due to invalid models (AUC-ROC &lt; 0.6)
            </span>
          </div>
        </div>
      )}

      {/* Data Source Note */}
      {kpiComparison?.data_source?.note && (
        <div className="mt-2 text-xs text-surface-500 italic">
          {kpiComparison.data_source.note}
        </div>
      )}
    </div>
  )
}
